import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CurrencyInput from "@/components/ui/currency-input";
import DateField from "@/components/ui/date-field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SubmitButton } from "@/components/ui/submit-button";
import { Textarea } from "@/components/ui/textarea";
import VehicleSelect from "@/components/ui/vehicle-select";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { RevenueCategory } from "@/interfaces";
import { errorHandler } from "@/lib/utils";
import { revenueCategoryService } from "@/services/api/revenueCategories";
import { vehicleRevenueService } from "@/services/api/vehicleRevenues";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

const formSchema = z.object({
  vehicle_id: z.number().min(1, "Selecione um veículo"),
  category_id: z.number().nullable(),
  invoice_number: z.string().nullable(),
  date: z.string().date("Data inválida"),
  amount: z.number().min(0, "Valor deve ser maior ou igual a zero"),
  observations: z.string().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

const RevenueForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<RevenueCategory[]>([]);

  useEffect(() => {
    revenueCategoryService
      .index()
      .then((res) => setCategories(res.data))
      .catch((err) => errorHandler(err, "Erro ao carregar categorias"));
  }, []);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vehicle_id: 0,
      category_id: null,
      invoice_number: "",
      date: format(new Date(), "yyyy-MM-dd"),
      amount: 0,
      observations: "",
    },
  });

  useEffect(() => {
    if (!id) return;
    vehicleRevenueService
      .show(Number(id))
      .then((res) => {
        const d = res.data;
        form.reset({
          vehicle_id: d.vehicle_id,
          category_id: d.category_id ?? null,
          invoice_number: d.invoice_number ?? "",
          date: d.date,
          amount: Number(d.amount),
          observations: d.observations ?? "",
        });
      })
      .catch((err) => errorHandler(err, "Erro ao carregar receita"));
  }, [id, form]);

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: vehicleRevenueService.create,
    updateService: vehicleRevenueService.update,
    onSuccess: () => navigate("/vehicle-revenues"),
  });

  const onSubmit = async (values: FormValues) => {
    await handleSubmit(values);
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>{id ? "Editar Receita" : "Nova Receita"}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="vehicle_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Veículo</FormLabel>
                  <VehicleSelect
                    value={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categoria</FormLabel>
                  <Select
                    value={field.value?.toString() ?? ""}
                    onValueChange={(val) =>
                      field.onChange(val ? Number(val) : null)
                    }
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma categoria (opcional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id.toString()}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="invoice_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nº Nota Fiscal</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DateField
              form={form}
              label="Data"
              name="date"
              className="flex flex-col"
              placeholder="Selecione uma data"
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valor (R$)</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="observations"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ""}
                      rows={4}
                      className="dark:border-white"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/vehicle-revenues")}
              >
                Cancelar
              </Button>
              <SubmitButton loading={loading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default RevenueForm;
